import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import type { PublishableProposalChange } from "work/proposals/negotiation/proposal-publishable-changes";
import { proposalSelectiveTranchePublicationApplies } from "work/proposals/negotiation/proposal-publishable-changes";
import { formatProposalChangeDetail } from "work/proposals/negotiation/proposal-change-description";
import { proposalTimelineFieldLabelForPath } from "work/proposals/negotiation/proposal-timeline";

/**
 * A concise, human summary of what a leader is about to send the counterparty. It is deliberately a
 * SUMMARY — field names plus the add/change/remove intent carried by each change's already-formatted
 * value — not a full redline. The tranche dialog renders it as a selectable list; the plain submit
 * confirmation renders it as an appended bullet list, so the leader sees the same set either way.
 */

/**
 * The field name a leader recognises for a publishable change. Prefers the curated
 * `HumanReadableProposalFieldName` mapping (`/discount/percent` → "Discount", the same label the
 * negotiation timeline shows) and falls back to the change's own path-derived label for a path the
 * curated map has never heard of, so an unknown field degrades to a readable name rather than blank.
 */
export function proposalSubmissionChangeFieldLabel(change: PublishableProposalChange): string {
  return proposalTimelineFieldLabelForPath(change.path) ?? change.label;
}

/** One grouped field in the summary: distinct changes on the same field collapse to a single row. */
export type ProposalSubmissionSummaryItem = {
  label: string;
  /** How many underlying publishable changes fall under this field. */
  count: number;
  /**
   * The formatted change when the field has exactly one change; omitted once grouped. Rendered through
   * the shared change vocabulary, so a value transition reads "Old scope → New scope" here exactly as
   * it does in the field's own change history and in the tranche dialog's redline.
   */
  detail?: string;
};

/**
 * Groups publishable changes by their curated field label, preserving first-seen order. Grouping is
 * what keeps the summary short: several fee-schedule or team edits read as one "Fee Schedule" / "Team"
 * row instead of a wall of near-identical lines.
 */
export function proposalSubmissionSummaryItems(
  changes: readonly PublishableProposalChange[],
): ProposalSubmissionSummaryItem[] {
  // A Map keeps insertion order, which IS first-seen order — so it is both the grouping and the
  // ordering, with no parallel list of labels to read back out of it.
  const grouped = new Map<string, PublishableProposalChange[]>();

  for (const change of changes) {
    const label = proposalSubmissionChangeFieldLabel(change);
    const bucket = grouped.get(label);
    if (bucket) {
      bucket.push(change);
    } else {
      grouped.set(label, [change]);
    }
  }

  return [...grouped].map(([label, bucket]) => ({
    label,
    count: bucket.length,
    detail: bucket.length === 1 ? describeSingleChange(bucket[0]) : undefined,
  }));
}

/**
 * One readable line per grouped field, e.g. `Description: Revised scope`, `Fee Schedule (3 changes)`,
 * or `Vendor Team Members: Removed`. A single change shows its value (which already reads as the
 * add/change/remove intent — "Removed", "Cleared", or the new value); a grouped field shows a count.
 */
export function proposalSubmissionSummaryLines(
  changes: readonly PublishableProposalChange[],
): string[] {
  return proposalSubmissionSummaryItems(changes).map((item) => {
    if (item.count > 1) return `${item.label} (${item.count} changes)`;
    return item.detail ? `${item.label}: ${item.detail}` : item.label;
  });
}

/**
 * One change described through the shared vocabulary: a transition when there is a prior value to show
 * ("Old scope → New scope"), the new value alone otherwise. Never a second formatter — see
 * `proposal-change-description.ts`.
 */
function describeSingleChange(change: PublishableProposalChange): string | undefined {
  return formatProposalChangeDetail({
    key: change.conflictKey,
    path: change.path,
    fieldLabel: change.label,
    action: "updated",
    previousText: change.previousValue,
    nextText: change.proposedValue,
  });
}

/**
 * True when the changes are being sent on top of an earlier submission, i.e. this is a re-submission.
 * A published change line is the evidence a submission already happened, which is exactly the
 * condition selective tranche publication keys on — so the two questions have one answer.
 */
export function proposalSubmissionIsResubmission(
  envelope: ProposalCollaborationEnvelope | undefined,
): boolean {
  return envelope ? proposalSelectiveTranchePublicationApplies(envelope) : false;
}

/**
 * Heading that frames the list, in the words that are actually true of it.
 *
 * Three cases, and each one used to be called "changes":
 *
 * - A re-submission is a delta since what the counterparty last saw.
 * - A revision of a hired work agreement is a delta too, but against the agreement both sides are
 *   already working under rather than against an earlier submission of this negotiation (#4852).
 * - A first-time offer's list is every populated field, because there is nothing behind it to differ
 *   from. Calling that "changes" told the reader something untrue about their own proposal, so it
 *   reads as a summary (#4846).
 */
export function proposalSubmissionSummaryHeading(
  isResubmission: boolean,
  revisesWorkAgreement = false,
): string {
  if (isResubmission) return "Changes since your last submission";
  if (revisesWorkAgreement) return "Changes to the Work Agreement";
  return "Proposal summary";
}

/**
 * Appends the concise summary to a plain confirm-dialog message so the submit confirmation shows what
 * is being sent even when the tranche dialog does not open (fewer than two changes). Mirrors the bullet
 * formatting the Save confirmation already uses, and returns the message untouched when there is
 * nothing publishable to summarise (e.g. a brand-new first submission), so those confirmations read
 * exactly as before.
 */
export function appendProposalSubmissionSummary(
  message: string,
  changes: readonly PublishableProposalChange[],
  isResubmission: boolean,
  revisesWorkAgreement = false,
): string {
  const lines = proposalSubmissionSummaryLines(changes);
  if (lines.length === 0) return message;

  const bullets = lines.map((line) => `• ${line}`).join("\n");
  const heading = proposalSubmissionSummaryHeading(isResubmission, revisesWorkAgreement);
  return `${message}\n\n${heading}:\n${bullets}`;
}
